import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { PromptSelect } from './PromptSelect';

import { CustomerSelect } from '@/components/CustomerSelect';
import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useUpdateModel } from '@/hooks/useUpdateModel';
import { FilterModelsDto, Model, ModelService } from '@/services/ModelsService';

const schema = z.object({
  prompt: z.string().min(1, 'Prompt is required'),
  service: z.nativeEnum(ModelService, { message: 'Service is required' }),
  modelVersion: z.string().min(1, 'Model version is required'),
  customer: z.string().min(1, 'Customer is required'),
});

type FormData = z.infer<typeof schema>;

export function UpdateModelForm({
  open,
  onClose,
  onSuccess,
  model,
  filter,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  model: Model | null;
  filter: FilterModelsDto;
}) {
  const {
    register,
    control,
    handleSubmit,

    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: '',
      service: ModelService.CREATE_NAME,
      modelVersion: '',
      customer: '',
    },
  });

  const { updateModel, isUpdating } = useUpdateModel(filter);

  useEffect(() => {
    if (model && open) {
      reset({
        prompt: String(model.promptId ?? ''),
        service: model.service,
        modelVersion: model.modelVersion,
        customer: model.customerId.toString(),
      });
    }
  }, [model, open, reset]);

  const onSubmit = handleSubmit(async (data: FormData) => {
    if (!model?.id) {
      toast.error('Missing model ID');
      return;
    }
    try {
      await updateModel({
        id: model.id,
        data: {
          service: data.service,
          modelVersion: data.modelVersion,
          promptId: parseInt(data.prompt, 10),
          customerId: parseInt(data.customer, 10),
        },
      });
      toast.success('Model updated');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error('Failed to update model');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onClose} modal={false}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Model</DialogTitle>
          <DialogDescription>Update model fields.</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <FormGroup error={{ message: errors.prompt?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="prompt">Prompt</RequiredLabel>
            <Controller
              name="prompt"
              control={control}
              render={({ field }) => (
                <PromptSelect
                  value={field.value}
                  onChange={(val) => {
                    field.onChange(String(val));
                  }}
                />
              )}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.service?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="service">Service</RequiredLabel>

            <Controller
              name="service"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger>
                    <SelectValue placeholder="Service" />
                  </SelectTrigger>

                  <SelectContent>
                    {Object.values(ModelService).map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          <FormGroup
            error={{ message: errors.modelVersion?.message, font: 'sm' }}
          >
            <RequiredLabel htmlFor="modelVersion">Model Version</RequiredLabel>
            <Input id="modelVersion" {...register('modelVersion')} />
          </FormGroup>

          <FormGroup error={{ message: errors.customer?.message, font: 'sm' }}>
            <RequiredLabel htmlFor="customer">Customer</RequiredLabel>
            <Controller
              name="customer"
              control={control}
              render={({ field }) => (
                <CustomerSelect
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                />
              )}
            />
          </FormGroup>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || isUpdating}>
              {isSubmitting || isUpdating ? (
                <Loader2 className="animate-spin" />
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
